import React from 'react';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';

import Client from './components/Client';
import Config from './components/ClientConfig';
import Message from './components/Message';
import MessagesFilters from './components/MessagesFilters';
import Visibility from './Visibility';

export default class MessagesList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client('none'),
      messages: null, // null => loading state (shows skeleton)
      addNew: false,
      filterData: undefined,
    };

    this.applyFilters = this.applyFilters.bind(this);
    this.toggleAdd = this.toggleAdd.bind(this);
    this.messageUpdated = this.messageUpdated.bind(this);
    this.getData = this.getData.bind(this);

    this.restricted = new Visibility('messages');
    this.filtersRef = React.createRef();
  }

  componentDidMount() {
    if (this.restricted.scope.visible) this.getData();
  }

  /**
   * Fetch initial list
   */
  async getData() {
    const queryData = { limit: 20, orderBy_createdOn: true };
    const base = Config.API_URL_MESSAGE;

    try {
      const data = await this.state.client.getData(base, queryData);

      if (data && Array.isArray(data.messages)) {
        this.setState({ messages: data.messages });
      } else if (Array.isArray(data)) {
        this.setState({ messages: data });
      } else {
        // Defensive: keep a safe fallback so render() never breaks
        this.setState({ messages: [] });
      }
    } catch (err) {
      console.error('MessagesList.getData error:', err);
      this.setState({ messages: [] });
    }
  }

  /**
   * Fetch with filters applied
   */
  async applyFilters(queryData) {
    const base = Config.API_URL_MESSAGE;

    try {
      const data = await this.state.client.getData(base, queryData);
      const nextMessages = Array.isArray(data?.messages)
        ? data.messages
        : Array.isArray(data)
        ? data
        : [];
      this.setState({ messages: nextMessages, filterData: queryData });
    } catch (err) {
      console.error('MessagesList.applyFilters error:', err);
      this.setState({ messages: [], filterData: queryData });
    }
  }

  toggleAdd() {
    if (!this.restricted.scope.edit) return;
    this.setState((s) => ({ addNew: !s.addNew }));
    window.scrollTo(0, 0);
  }

  async messageUpdated() {
    const queryData = this.state.filterData ? this.state.filterData : {};

    // Show skeleton while refreshing
    this.setState({ messages: null, addNew: false }, async () => {
      try {
        if (Object.keys(queryData).length) {
          await this.applyFilters(queryData);
        } else {
          await this.getData();
        }
      } finally {
        window.scrollTo(0, 0);
      }
    });

    if (this.filtersRef?.current) {
      this.filtersRef.current.setState({ addNew: false });
    }
  }

  render() {
    if (!this.restricted.scope.visible) return <div />;

    const { messages } = this.state;
    const isArray = Array.isArray(messages);
    const counter = isArray ? messages.length : 0;

    const list = isArray
      ? messages.map((item) => (
          <Message
            key={item.iref}
            item={item}
            onUpdated={this.messageUpdated}
            allowEdit={this.restricted.scope.edit}
            API_URL_MESSAGES={this.props.API_URL_MESSAGES}
          />
        ))
      : null;

    return (
      <div style={{ backgroundColor: '#273442', minHeight: '100%' }}>
        <div className="sticky">
          <MessagesFilters
            ref={this.filtersRef}
            applyFilters={this.applyFilters}
            toggleAdd={this.toggleAdd}
            counter={counter}
            allowEdit={this.restricted.scope.edit}
          />
        </div>

        {this.state.addNew ? (
          <Message
            key={0}
            item={undefined}
            isNew={true}
            onUpdated={this.messageUpdated}
            allowEdit={this.restricted.scope.edit}
            API_URL_MESSAGES={this.props.API_URL_MESSAGES}
          />
        ) : null}

        {messages === null ? (
          <Box sx={{ p: 2 }}>
            <Skeleton animation="wave" variant="rounded" width={800} height={160} />
            <Skeleton animation="wave" variant="rounded" width={800} height={160} sx={{ mt: 1 }} />
          </Box>
        ) : Array.isArray(messages) && messages.length === 0 ? (
          <Box sx={{ p: 3, color: '#e3e3e5', opacity: 0.8 }}>
            No messages match your filters. Try clearing or adjusting filters.
          </Box>
        ) : (
          list
        )}
      </div>
    );
  }
}
